import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Backdrop,
  FormControl,
  Select,
  MenuItem,
  TableSortLabel
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Search as SearchIcon,
  Assignment as AssignmentIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

// Import API handlers
import {
  getHotelAndHost,
  updateHotelStatus,
  assignExistingUser,
  assignNewUser,
  searchUsers,
  setHotelPaymentMode,
  setHotelPrepaidDiscount
} from '../api/adminPanelApi';

const AdminPanelUserAssign = () => {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [allHotelsData, setAllHotelsData] = useState([]); // Store original API data for search
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [dialogTab, setDialogTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [actionLoading, setActionLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [discountDialog, setDiscountDialog] = useState({ open: false, hotel: null, value: '', type: 'percent' });

  // Function to format prepaid discount display
  const formatPrepaidDiscount = (prepaidDiscount) => {
    if (!prepaidDiscount || prepaidDiscount === null) {
      return '0';
    }
    if (prepaidDiscount.includes('%')) {
      return prepaidDiscount;
    }
    return `MRP ${prepaidDiscount}`;
  };

  // Fetch hotels data on component mount
  useEffect(() => {
    fetchHotelsData();
  }, []);

  // Fetch hotels and hosts data from API
  const fetchHotelsData = async () => {
    setLoading(true);
    try {
      const result = await getHotelAndHost();
      if (result.success) {
        setHotels(result.data.data);
        setAllHotelsData(result.data.data);
        setSnackbar({
          open: true,
          message: `Loaded ${result.data.data.length} hotels successfully`,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: result.error,
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to fetch hotels data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort function
  const sortData = (data, config) => {
    if (!config.key) return data;
    
    return [...data].sort((a, b) => {
      let aValue = a[config.key];
      let bValue = b[config.key];
      
      // Handle different data types
      if (config.key === 'hotel_id' || config.key === 'host_id') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue || '').toLowerCase();
      }
      
      if (aValue < bValue) {
        return config.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return config.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Filter hotels based on search
  const filteredHotels = useMemo(() => {
    let filtered = Array.isArray(hotels) ? hotels : [];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(hotel =>
        hotel.hotel_id.toString().includes(query) ||
        hotel.hotel_name.toLowerCase().includes(query) ||
        hotel.email?.toLowerCase().includes(query) ||
        hotel.phone?.includes(query) ||
        hotel.host_id.toString().includes(query)
      );
    }
    return sortData(filtered, sortConfig);
  }, [hotels, searchQuery, sortConfig]);

  // Handle hotel activation/deactivation
  const handleToggleHotelStatus = async (hotel) => {
    setActionLoading(true);
    try {
      const newStatus = hotel.isActive === 1 ? 'inactive' : 'active';
      const result = await updateHotelStatus(hotel.hotel_id, hotel.host_id, newStatus);
      
      if (result.success) {
        // Update local state
        setHotels(prev => prev.map(h =>
          h.hotel_id === hotel.hotel_id
            ? { ...h, isActive: hotel.isActive === 1 ? 0 : 1 }
            : h
        ));
        
        const action = hotel.isActive === 1 ? 'deactivated' : 'activated';
        setSnackbar({
          open: true,
          message: `${hotel.hotel_name} has been ${action} successfully`,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: result.error,
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to update hotel status',
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle payment mode change
  const handlePaymentModeChange = async (hotel, newPaymentMode) => {
    setActionLoading(true);
    try {
      const result = await setHotelPaymentMode(hotel.hotel_id, newPaymentMode);
      
      if (result.success) {
        // Update local state
        setHotels(prev => prev.map(h =>
          h.hotel_id === hotel.hotel_id
            ? { ...h, payment_mode: newPaymentMode }
            : h
        ));
        
        const modeText = newPaymentMode === 1 ? 'Prepaid Only' : 'Both Prepaid and Postpaid';
        setSnackbar({
          open: true,
          message: `${hotel.hotel_name} payment mode updated to ${modeText} successfully`,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: result.error,
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to update payment mode',
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Open discount dialog for specific hotel
  const handleOpenDiscount = (hotel) => {
    setDiscountDialog({ open: true, hotel, value: '', type: 'percent' });
  };

  const handleCloseDiscount = () => {
    setDiscountDialog({ open: false, hotel: null, value: '', type: 'percent' });
  };

  const handleSubmitDiscount = async () => {
    if (!discountDialog.hotel) return;
    const val = Number(discountDialog.value);
    if (Number.isNaN(val) || val <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid discount value', severity: 'error' });
      return;
    }
    setActionLoading(true);
    try {
      const resp = await setHotelPrepaidDiscount(discountDialog.hotel.hotel_id, val, discountDialog.type);
      if (resp.success && resp.data?.status) {
        setSnackbar({ open: true, message: 'Discount updated successfully', severity: 'success' });
        handleCloseDiscount();
      } else {
        setSnackbar({ open: true, message: resp.data?.message || 'Failed to update discount', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to update discount', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Open assignment dialog
  const handleOpenAssignment = (hotel) => {
    setSelectedHotel(hotel);
    setOpenDialog(true);
    setDialogTab(0);
    setUserSearchQuery('');
    setSearchResults([]);
    setNewUserData({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  };

  // Close assignment dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedHotel(null);
    setUserSearchQuery('');
    setSearchResults([]);
    setNewUserData({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setDialogTab(newValue);
    setUserSearchQuery('');
    setSearchResults([]);
    setNewUserData({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  };



  // Auto-search as user types
  useEffect(() => {
    if (userSearchQuery.length >= 2) {
      const timeoutId = setTimeout(() => {
        const results = searchUsers(allHotelsData, userSearchQuery);
        setSearchResults(results);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
    }
  }, [userSearchQuery, allHotelsData]);

  // Assign existing user to hotel
  const handleAssignExistingUser = async (user) => {
    setActionLoading(true);
    try {
      const result = await assignExistingUser(selectedHotel.hotel_id, user.email);
      
      if (result.success) {
        // Update local state
        setHotels(prev => prev.map(hotel =>
          hotel.hotel_id === selectedHotel.hotel_id
            ? { ...hotel, host_id: user.host_id, email: user.email, phone: user.phone }
            : hotel
        ));
        
        setSnackbar({
          open: true,
          message: `User ${user.email} has been assigned to ${selectedHotel.hotel_name} successfully`,
          severity: 'success'
        });
        handleCloseDialog();
      } else {
        setSnackbar({
          open: true,
          message: result.error,
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to assign existing user',
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Assign new user to hotel
  const handleAssignNewUser = async () => {
    setActionLoading(true);
    try {
      const result = await assignNewUser(
        selectedHotel.hotel_id,
        newUserData.name,
        newUserData.email,
        newUserData.phone,
        newUserData.password,
        newUserData.confirmPassword
      );
      
      if (result.success) {
        // Refresh hotels data to get updated information
        await fetchHotelsData();
        
        setSnackbar({
          open: true,
          message: `New user ${newUserData.email} has been created and assigned to ${selectedHotel.hotel_name} successfully`,
          severity: 'success'
        });
        handleCloseDialog();
      } else {
        setSnackbar({
          open: true,
          message: result.error,
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to create and assign new user',
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle input changes for new user
  const handleNewUserChange = (field, value) => {
    setNewUserData(prev => ({ ...prev, [field]: value }));
  };

  // Calculate statistics
  const safeHotels = Array.isArray(hotels) ? hotels : [];
  const totalHotels = safeHotels.length;
  const activeHotels = safeHotels.filter(h => h.isActive === 1).length;
  const inactiveHotels = safeHotels.filter(h => h.isActive === 0).length;
  const uniqueHosts = new Set(safeHotels.filter(h => h.host_id).map(h => h.host_id)).size;

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Loading Backdrop */}
      <Backdrop open={loading || actionLoading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={() => navigate('/')} 
              sx={{ 
                color: '#FF7700',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 119, 0, 0.1)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#2E4057' }}>
              Admin Panel - User Assignment
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchHotelsData}
            disabled={loading}
            sx={{ 
              borderColor: '#FF7700',
              color: '#FF7700',
              '&:hover': {
                borderColor: '#e65100',
                backgroundColor: 'rgba(255, 119, 0, 0.04)'
              }
            }}
          >
            Refresh Data
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage hotel user assignments and property activation status
        </Typography>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Search hotels by ID, name, user email, phone, or host ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
          }}
          sx={{ 
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#FF7700'
                }
              },
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#FF7700'
                }
              }
            }
          }}
        />
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
            border: '1px solid #90caf9',
            boxShadow: '0 2px 8px rgba(33, 150, 243, 0.1)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BusinessIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#1565c0', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                    {totalHotels}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1976d2' }}>Total Hotels</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
            border: '1px solid #a5d6a7',
            boxShadow: '0 2px 8px rgba(76, 175, 80, 0.1)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircleIcon sx={{ fontSize: 24, color: '#388e3c' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#2e7d32', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                    {activeHotels}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#388e3c' }}>Active Hotels</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
            border: '1px solid #ffcc80',
            boxShadow: '0 2px 8px rgba(255, 152, 0, 0.1)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BlockIcon sx={{ fontSize: 24, color: '#f57c00' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#ef6c00', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                    {inactiveHotels}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#f57c00' }}>Inactive Hotels</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%)',
            border: '1px solid #f48fb1',
            boxShadow: '0 2px 8px rgba(233, 30, 99, 0.1)'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PersonIcon sx={{ fontSize: 24, color: '#c2185b' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#ad1457', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                    {uniqueHosts}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#c2185b' }}>Unique Hosts</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Hotels Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ p: 2, py: 1.5, borderBottom: '1px solid #e0e0e0' }}>
                      <Typography variant="h6" fontWeight={600} sx={{ color: '#2E4057', fontSize: '1.1rem' }}>
              Hotels & User Assignments ({Array.isArray(filteredHotels) ? filteredHotels.length : 0} hotels)
            </Typography>
        </Box>
        
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ height: '48px' }}>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', width: '120px', padding: '8px 16px' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'hotel_id'}
                    direction={sortConfig.key === 'hotel_id' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('hotel_id')}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    Hotel ID
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', width: '200px', padding: '8px 16px' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'hotel_name'}
                    direction={sortConfig.key === 'hotel_name' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('hotel_name')}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    Hotel Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', width: '100px', padding: '8px 16px' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'host_id'}
                    direction={sortConfig.key === 'host_id' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('host_id')}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    Host ID
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', width: '180px', padding: '8px 16px' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'email'}
                    direction={sortConfig.key === 'email' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('email')}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', width: '140px', padding: '8px 16px' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'phone'}
                    direction={sortConfig.key === 'phone' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('phone')}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    Phone
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', width: '180px', padding: '8px 16px' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'payment_mode'}
                    direction={sortConfig.key === 'payment_mode' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('payment_mode')}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    Payment Mode
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', width: '100px', padding: '8px 16px' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'isActive'}
                    direction={sortConfig.key === 'isActive' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('isActive')}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', width: '200px', padding: '8px 16px' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredHotels.map((hotel) => (
                <TableRow 
                  key={hotel.hotel_id} 
                  hover
                  sx={{ 
                    opacity: hotel.isActive === 1 ? 1 : 0.6,
                    backgroundColor: hotel.isActive === 1 ? 'inherit' : '#f5f5f5'
                  }}
                >
                  <TableCell>
                    <Chip 
                      label={hotel.hotel_id} 
                      size="small" 
                      sx={{ backgroundColor: '#FF7700', color: 'white' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {hotel.hotel_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={hotel.host_id || 'N/A'} 
                      size="small" 
                      sx={{ backgroundColor: '#43e97b', color: 'white' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{hotel.email || 'No email'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{hotel.phone || 'No phone'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 160, width: '100%' }}>
                      <Select
                        value={hotel.payment_mode !== undefined ? hotel.payment_mode : 1}
                        onChange={(e) => handlePaymentModeChange(hotel, e.target.value)}
                        disabled={actionLoading}
                        sx={{
                          backgroundColor: hotel.payment_mode === 1 ? '#e8f5e8' : '#fff3e0',
                          '& .MuiSelect-select': {
                            color: hotel.payment_mode === 1 ? '#2e7d32' : '#f57c00',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            padding: '8px 12px'
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: hotel.payment_mode === 1 ? '#4caf50' : '#ff9800'
                          }
                        }}
                      >
                        <MenuItem value={1} sx={{ fontSize: '0.75rem' }}>Prepaid Only</MenuItem>
                        <MenuItem value={0} sx={{ fontSize: '0.75rem' }}>Both Prepaid & Postpaid</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={hotel.isActive === 1 ? 'Active' : 'Inactive'} 
                      size="small" 
                      sx={{ 
                        backgroundColor: hotel.isActive === 1 ? '#4caf50' : '#f44336',
                        color: 'white'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AssignmentIcon />}
                        onClick={() => handleOpenAssignment(hotel)}
                        sx={{ 
                          borderColor: '#FF7700',
                          color: '#FF7700',
                          '&:hover': {
                            borderColor: '#e65100',
                            backgroundColor: 'rgba(255, 119, 0, 0.04)'
                          }
                        }}
                      >
                        Assign User
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleOpenDiscount(hotel)}
                        sx={{
                          backgroundColor: '#FFC07A',
                          color: '#000',
                          '&:hover': { backgroundColor: '#FF9E42' },
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1.5
                        }}
                      >
                        <Typography variant="body2" fontWeight={500}>
                          Set Discount
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: '#4caf50',
                            color: 'white',
                            borderRadius: 1,
                            px: 1,
                            py: 0.25,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            minWidth: '32px',
                            textAlign: 'center',
                            border: '1px solid #2e7d32'
                          }}
                        >
                          {formatPrepaidDiscount(hotel.prepaidDiscount)}
                        </Box>
                      </Button>
                      
                      {hotel.isActive === 1 ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<BlockIcon />}
                          onClick={() => handleToggleHotelStatus(hotel)}
                          sx={{ 
                            borderColor: '#f44336',
                            color: '#f44336',
                            '&:hover': {
                              borderColor: '#d32f2f',
                              backgroundColor: 'rgba(244, 67, 54, 0.04)'
                            }
                          }}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleToggleHotelStatus(hotel)}
                          sx={{ 
                            borderColor: '#4caf50',
                            color: '#4caf50',
                            '&:hover': {
                              borderColor: '#388e3c',
                              backgroundColor: 'rgba(76, 175, 80, 0.04)'
                            }
                          }}
                        >
                          Activate
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredHotels.length === 0 && !loading && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {searchQuery ? 'No hotels found matching your search criteria.' : 'No hotels available.'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* User Assignment Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssignmentIcon sx={{ color: '#FF7700' }} />
            <Typography variant="h6" fontWeight={600}>
              Assign User to {selectedHotel?.hotel_name}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          <Tabs 
            value={dialogTab} 
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Assign Existing User" />
            <Tab label="Create New User" />
          </Tabs>
          
          {/* Existing User Tab */}
          {dialogTab === 0 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Search for an existing user to assign to this hotel
              </Typography>
              
              <TextField
                fullWidth
                placeholder="Search by Host ID or Email..."
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ mb: 3 }}
              />
              
              {searchResults.length > 0 && (
                <List>
                  {searchResults.map((user, index) => (
                    <React.Fragment key={`${user.host_id}_${user.email}`}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <PersonIcon sx={{ color: 'text.secondary' }} />
                              <Typography variant="subtitle1" fontWeight={500}>
                                {user.email}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                <strong>Host ID:</strong> {user.host_id}
                              </Typography>
                              {user.phone && (
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Phone:</strong> {user.phone}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Button
                            variant="contained"
                            startIcon={<AssignmentIcon />}
                            onClick={() => handleAssignExistingUser(user)}
                            sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#388e3c' } }}
                          >
                            Assign
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < searchResults.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
              
              {userSearchQuery && userSearchQuery.length >= 2 && searchResults.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No users found matching your search criteria.
                </Alert>
              )}

              {userSearchQuery && userSearchQuery.length < 2 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Please enter at least 2 characters to search for users.
                </Alert>
              )}
            </Box>
          )}
          
          {/* New User Tab */}
          {dialogTab === 1 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create a new user account and assign them to this hotel
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={newUserData.name}
                    onChange={(e) => handleNewUserChange('name', e.target.value)}
                    placeholder="Enter full name"
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={newUserData.email}
                    onChange={(e) => handleNewUserChange('email', e.target.value)}
                    placeholder="Enter email address"
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={newUserData.phone}
                    onChange={(e) => handleNewUserChange('phone', e.target.value)}
                    placeholder="Enter phone number"
                    InputProps={{
                      startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={newUserData.password}
                    onChange={(e) => handleNewUserChange('password', e.target.value)}
                    placeholder="Enter password (min 6 characters)"
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      )
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={newUserData.confirmPassword}
                    onChange={(e) => handleNewUserChange('confirmPassword', e.target.value)}
                    placeholder="Confirm password"
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      )
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          
          {dialogTab === 1 && (
            <Button
              variant="contained"
              onClick={handleAssignNewUser}
              disabled={!newUserData.name || !newUserData.email || !newUserData.phone || !newUserData.password || !newUserData.confirmPassword}
              sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#388e3c' } }}
            >
              Create & Assign User
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Discount Dialog */}
      <Dialog open={discountDialog.open} onClose={handleCloseDiscount} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" fontWeight={600}>Set Prepaid Discount</Typography>
          {discountDialog.hotel && (
            <Typography variant="body2" color="text.secondary">Hotel: {discountDialog.hotel.hotel_name} (ID: {discountDialog.hotel.hotel_id})</Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Select value={discountDialog.type} onChange={(e)=>setDiscountDialog(prev=>({...prev, type:e.target.value}))}>
                  <MenuItem value="percent">Percent</MenuItem>
                  <MenuItem value="mrp">MRP</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Value"
                type="number"
                value={discountDialog.value}
                onChange={(e)=>setDiscountDialog(prev=>({...prev, value:e.target.value}))}
                placeholder={discountDialog.type === 'percent' ? 'Enter percent (e.g., 10)' : 'Enter MRP amount'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDiscount} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={handleSubmitDiscount} sx={{ backgroundColor: '#FFC07A', color:'#000', '&:hover':{ backgroundColor:'#FF9E42' }}}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPanelUserAssign; 